/**
 * Single footprint placement on the PCB — the SVG primitive consumed
 * by `PCBCanvas`.
 *
 * Two render modes:
 *
 *   * **Rich** (when `placement.pads` and/or `placement.graphics` is
 *     populated by the .kicad_pcb parser): paints the courtyard /
 *     fab / silk outline + per-pad SVG primitive (rect / circle /
 *     oval / roundrect) + drill aperture. The outline scales with
 *     the actual footprint, not a kind-keyed guess.
 *
 *   * **Stub** (legacy / no-pcb-overlay): kind-keyed bounding rect.
 *     Same data the schematic-only IR carries — keeps schematic-only
 *     uploads visually consistent with the pre-Phase-16 PCB tab.
 *
 * Visual layers, painted bottom-up:
 *
 *   1. Hover/select halo — phosphor-cyan ring that softly pulses when
 *      the refdes is in `canvasFocusStore.selectedRefdes` (sticky) or
 *      `hoveredRefdes` (transient).
 *   2. Footprint outline — courtyard + fab + silkscreen lines/circles
 *      (rich mode), or kind-keyed bounding rect (stub mode).
 *   3. Pads — per-pad copper + drill aperture (rich mode only).
 *   4. Refdes label — JetBrains Mono, counter-rotated to stay upright.
 *
 * **Why all this lives in SVG, not CSS.** SVG `transform` is part of
 * the viewBox, so the entire stack scales with pan/zoom for free. CSS
 * transforms would need manual matrix tracking against viewport state,
 * which is the bug factory we're avoiding.
 */
import type { FootprintGraphic, PCBPlacement, Pad } from "@models/circuit-ir";
import { useTheme } from "@mui/material/styles";
import { memo } from "react";

import { PCB_LAYER_PALETTE, arcPathD, footprintSize } from "./pcbStyle";

export interface PCBFootprintProps {
  refdes: string;
  kind: string;
  value: string | null;
  placement: PCBPlacement;
  isHovered: boolean;
  isSelected: boolean;
  /** Keyboard focus ring — `true` when the canvas keyboard cursor is
   *  parked on this footprint. Renders a distinct phosphor ring so
   *  the user can tell "I have arrow-key focus on R5" apart from
   *  "I have R5 clicked". */
  isFocused?: boolean;
  /** Whether the footprint's layer is currently visible. When false,
   *  the footprint renders at 25% opacity with no interactivity — the
   *  user can still see "something's there on the other side" without
   *  it stealing focus. */
  layerVisible: boolean;
  /** Whether refdes labels are visible. Defaults to true. */
  showLabel?: boolean;
  /** Hover handlers wire `canvasFocusStore`. */
  onHoverStart: (refdes: string) => void;
  onHoverEnd: (refdes: string) => void;
  /** Click handler — receives the keyboard modifier state so the canvas
   *  can route to `setSelected` / `addToSelection` / `toggleSelection`
   *  for plain / shift / ⌘ clicks respectively. */
  onClick: (
    refdes: string,
    modifiers: { shift: boolean; meta: boolean },
  ) => void;
}

/** Pick a stroke colour for a footprint-graphic layer. */
function graphicStroke(layer: string): string {
  if (layer.endsWith("CrtYd")) return PCB_LAYER_PALETTE.crtyd;
  if (layer.endsWith("Fab")) return PCB_LAYER_PALETTE.fab;
  if (layer.endsWith("SilkS")) return PCB_LAYER_PALETTE.silk;
  return PCB_LAYER_PALETTE.silk;
}

/** SVG primitive for a single pad. Through-hole pads draw an extra
 *  dark drill aperture inside the copper. */
function PadShape({ pad, layerStroke }: { pad: Pad; layerStroke: string }) {
  const [x, y] = pad.at;
  const [w, h] = pad.size;
  const isThroughHole = pad.type === "thru_hole" || pad.type === "np_thru_hole";
  // For through-hole pads, KiCad lists *.Cu (all copper layers); the
  // pad shows on whichever side. SMD pads list a specific layer. We
  // colour-key by the pad's effective side, falling back to the
  // placement layer when it's *.Cu.
  const padCopper =
    isThroughHole || pad.layers.some((l) => l === "*.Cu")
      ? layerStroke
      : pad.layers.some((l) => l === "B.Cu")
        ? PCB_LAYER_PALETTE.bCu
        : layerStroke;

  const drillD = pad.drill ?? 0;
  const transform = pad.rotation
    ? `translate(${x} ${y}) rotate(${-pad.rotation})`
    : `translate(${x} ${y})`;

  return (
    <g transform={transform}>
      {pad.shape === "circle" || pad.shape === "oval" ? (
        <ellipse
          cx={0}
          cy={0}
          rx={w / 2}
          ry={h / 2}
          fill={padCopper}
          fillOpacity={0.85}
          stroke={padCopper}
          strokeWidth={0.05}
        />
      ) : (
        <rect
          x={-w / 2}
          y={-h / 2}
          width={w}
          height={h}
          rx={pad.shape === "roundrect" ? Math.min(w, h) * 0.25 : 0}
          fill={padCopper}
          fillOpacity={0.85}
          stroke={padCopper}
          strokeWidth={0.05}
        />
      )}
      {isThroughHole && drillD > 0 && (
        <circle cx={0} cy={0} r={drillD / 2} fill={PCB_LAYER_PALETTE.drill} />
      )}
    </g>
  );
}

/** SVG primitive for a single footprint graphic (line / circle / arc /
 *  text). */
function GraphicShape({ g }: { g: FootprintGraphic }) {
  const stroke = graphicStroke(g.layer);
  // Silk reads at lower contrast than fab/courtyard so cluttered
  // libraries don't drown the copper.
  const opacity = g.layer.endsWith("SilkS") ? 0.55 : 0.7;
  if (g.kind === "line" && g.start && g.end) {
    return (
      <line
        x1={g.start[0]}
        y1={g.start[1]}
        x2={g.end[0]}
        y2={g.end[1]}
        stroke={stroke}
        strokeOpacity={opacity}
        strokeWidth={g.width}
        strokeLinecap="round"
      />
    );
  }
  if (g.kind === "circle" && g.center && g.radius != null) {
    return (
      <circle
        cx={g.center[0]}
        cy={g.center[1]}
        r={g.radius}
        fill="none"
        stroke={stroke}
        strokeOpacity={opacity}
        strokeWidth={g.width}
      />
    );
  }
  if (g.kind === "arc" && g.start && g.arc_mid && g.end) {
    return (
      <path
        d={arcPathD(g.start, g.arc_mid, g.end)}
        fill="none"
        stroke={stroke}
        strokeOpacity={opacity}
        strokeWidth={g.width}
        strokeLinecap="round"
      />
    );
  }
  if (g.kind === "text" && g.start && g.text) {
    const [tx, ty] = g.start;
    const size = g.text_size?.[1] ?? 1.0;
    return (
      <text
        x={tx}
        y={ty}
        fontSize={size}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={stroke}
        fillOpacity={opacity}
        transform={
          g.rotation ? `rotate(${-g.rotation} ${tx} ${ty})` : undefined
        }
        style={{
          fontFamily: "ui-monospace, SFMono-Regular, monospace",
          userSelect: "none",
          letterSpacing: "-0.04em",
        }}
      >
        {g.text}
      </text>
    );
  }
  if (g.kind === "polygon" && g.polygon && g.polygon.length >= 3) {
    return (
      <polygon
        points={g.polygon.map((p) => `${p[0]},${p[1]}`).join(" ")}
        fill={stroke}
        fillOpacity={opacity * 0.5}
        stroke={stroke}
        strokeOpacity={opacity}
        strokeWidth={g.width}
      />
    );
  }
  return null;
}

function PCBFootprintImpl({
  refdes,
  kind,
  value,
  placement,
  isHovered,
  isSelected,
  isFocused = false,
  layerVisible,
  showLabel = true,
  onHoverStart,
  onHoverEnd,
  onClick,
}: PCBFootprintProps) {
  const theme = useTheme();
  const isFront = placement.layer === "F.Cu";
  const layerStroke = isFront ? PCB_LAYER_PALETTE.fCu : PCB_LAYER_PALETTE.bCu;
  const pads = placement.pads ?? [];
  const graphics = placement.graphics ?? [];
  const hasRichGeometry = pads.length > 0 || graphics.length > 0;
  // Body box — kind-keyed bounding rect when no rich data, else a
  // looser bound derived from the pad/courtyard envelope so the
  // halo + click target match the real footprint.
  //
  // KiCad footprints don't put their origin at the pad envelope's
  // centroid — origins commonly sit on pin 1 or a part-defined
  // anchor. We have to track the bbox CENTER (cx, cy) in addition
  // to its size so the halo / body wash / refdes label render where
  // the pads actually are, not at the footprint origin. Without
  // this every off-center footprint (anything with origin != pad
  // centroid: SOICs anchored on pin 1, asymmetric connectors, …)
  // shows the halo offset from the copper, which read as "overlay
  // doesn't align" in the user-facing report.
  let { w, h } = footprintSize(kind);
  let cx = 0;
  let cy = 0;
  if (hasRichGeometry) {
    // Track running min/max directly — a `Math.min(...xs)` spread
    // blows the JS argument-count fast path on dense footprints (BGAs
    // with ≥10k pads) and crashes some engines outright at ~65k. The
    // plain loop is O(n) with constant memory and no allocation.
    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    let touched = false;
    const observe = (x: number, y: number) => {
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      touched = true;
    };
    for (const p of pads) {
      observe(p.at[0] - p.size[0] / 2, p.at[1] - p.size[1] / 2);
      observe(p.at[0] + p.size[0] / 2, p.at[1] + p.size[1] / 2);
    }
    for (const g of graphics) {
      if (g.kind === "line" && g.start && g.end) {
        observe(g.start[0], g.start[1]);
        observe(g.end[0], g.end[1]);
      } else if (g.kind === "circle" && g.center && g.radius != null) {
        observe(g.center[0] - g.radius, g.center[1] - g.radius);
        observe(g.center[0] + g.radius, g.center[1] + g.radius);
      }
    }
    if (touched) {
      w = Math.max(0.5, maxX - minX);
      h = Math.max(0.5, maxY - minY);
      cx = (minX + maxX) / 2;
      cy = (minY + maxY) / 2;
    }
  }
  const [px, py] = placement.at;

  // Visual emphasis flag — sticks together "any focus state" so the
  // halo and the boost-stroke can be controlled by one branch.
  const focused = isHovered || isSelected;

  // Halo grows ~0.6mm beyond the body. Sticky select gets a slightly
  // brighter halo than transient hover so the eye can find a pinned
  // selection while the user moves elsewhere.
  const haloPad = 0.6;
  const haloOpacity = isSelected ? 0.85 : isHovered ? 0.55 : 0;

  // Pin-1 sits at footprint-local (-w/2 + small inset, -h/2 + small inset).
  // The translation/rotation in the outer <g> brings it into world space.
  const pin1Inset = Math.min(w, h) * 0.18;

  // Accessible name — what a screen reader reads when this footprint
  // gets keyboard focus. Construct human-friendly: "R5, 10k resistor
  // on top layer" beats "R5". When kind is "other" fall back to
  // just the refdes + value.
  const layerLabel =
    placement.layer === "F.Cu" ? "top layer" : "bottom layer";
  const kindLabel = kind && kind !== "other" ? `${kind} ` : "";
  const valuePart = value ? `, ${value}` : "";
  const ariaName = `${refdes}${valuePart}, ${kindLabel}on ${layerLabel}`;

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard handling lives on the canvas root via usePcbKeyboardNav
    <g
      transform={`translate(${px} ${py}) rotate(${-placement.rotation})`}
      style={{
        cursor: layerVisible ? "pointer" : "default",
        opacity: layerVisible ? 1 : 0.25,
        pointerEvents: layerVisible ? "auto" : "none",
        transition: theme.motion.base,
      }}
      onPointerEnter={(e) => {
        if (!layerVisible) return;
        e.stopPropagation();
        onHoverStart(refdes);
      }}
      onPointerLeave={(e) => {
        if (!layerVisible) return;
        e.stopPropagation();
        onHoverEnd(refdes);
      }}
      onClick={(e) => {
        if (!layerVisible) return;
        // Stop propagation so the click doesn't also start a pan.
        e.stopPropagation();
        onClick(refdes, {
          shift: e.shiftKey,
          meta: e.metaKey || e.ctrlKey,
        });
      }}
      data-refdes={refdes}
      data-layer={placement.layer}
      role="button"
      aria-label={ariaName}
    >
      {/* Accessible-name backing for assistive tech — SVG title is
          the standard mechanism for naming a graphic element when
          aria-label isn't picked up. Belt + braces with the aria-label
          above. */}
      <title>{ariaName}</title>

      {/* Invisible hit area — covers the full bounding box (plus halo
          padding) so hover/click work anywhere over the visible
          footprint, not just on pads/silk strokes. Without this, rich-
          mode footprints have dead pixels between pads because the
          body wash below carries `pointerEvents="none"` to keep its
          tint from hijacking pad clicks. `fill="transparent"` is hit-
          testable in SVG; `fill="none"` is not. */}
      <rect
        x={cx - w / 2 - haloPad}
        y={cy - h / 2 - haloPad}
        width={w + haloPad * 2}
        height={h + haloPad * 2}
        fill="transparent"
      />

      {/* Keyboard focus ring — dashed phosphor outline that's visually
          distinct from the click-selection halo (solid + glow) and the
          hover halo (solid + dim). Pulses softly so a focused-but-
          unselected footprint stays discoverable. */}
      {isFocused && (
        <rect
          x={cx - w / 2 - haloPad - 0.2}
          y={cy - h / 2 - haloPad - 0.2}
          width={w + haloPad * 2 + 0.4}
          height={h + haloPad * 2 + 0.4}
          rx={0.45}
          fill="none"
          stroke={PCB_LAYER_PALETTE.pad}
          strokeWidth={0.14}
          strokeDasharray="0.5 0.35"
          pointerEvents="none"
          style={{
            filter: `drop-shadow(0 0 0.4mm ${PCB_LAYER_PALETTE.pad})`,
            animation: "pcbFocusPulse 1.6s ease-in-out infinite",
          }}
        />
      )}

      {/* Halo — phosphor-cyan glow ring, painted under the body so the
          body's stroke reads cleanly on top. */}
      {haloOpacity > 0 && (
        <rect
          x={cx - w / 2 - haloPad}
          y={cy - h / 2 - haloPad}
          width={w + haloPad * 2}
          height={h + haloPad * 2}
          rx={0.4}
          fill="none"
          stroke={PCB_LAYER_PALETTE.pad}
          strokeWidth={isSelected ? 0.18 : 0.12}
          pointerEvents="none"
          style={{
            opacity: haloOpacity,
            filter: `drop-shadow(0 0 ${isSelected ? 0.5 : 0.3}mm ${PCB_LAYER_PALETTE.pad})`,
            transition: theme.motion.base,
          }}
        />
      )}

      {hasRichGeometry ? (
        <>
          {/* Rich-mode body tint — same layer-coloured wash as the
              stub mode but sized to the pad/courtyard envelope, so the
              hover halo and click target read correctly. Offset by
              (cx, cy) so the wash sits over the pads even when the
              footprint origin isn't at the pad centroid. */}
          <rect
            x={cx - w / 2}
            y={cy - h / 2}
            width={w}
            height={h}
            rx={0.2}
            fill={layerStroke}
            fillOpacity={focused ? 0.18 : 0.08}
            stroke="none"
            pointerEvents="none"
          />
          {/* Footprint outline — courtyard / fab / silk lines + circles.
              Rendered on top of the body wash so silkscreen reads. */}
          {graphics.map((g, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: footprint graphics are immutable, render-stable IR data
            <GraphicShape key={`g${i}`} g={g} />
          ))}
          {/* Pads — copper + drill aperture for through-hole. */}
          {pads.map((p, i) => (
            <PadShape
              key={`p${i}-${p.number}`}
              pad={p}
              layerStroke={layerStroke}
            />
          ))}
          {/* Pin-1 highlight — small phosphor cyan ring around pad #1
              (or the first pad if no #1 exists). Helps engineers spot
              orientation at a glance. */}
          {(() => {
            const pin1 = pads.find((p) => p.number === "1") ?? pads[0];
            if (!pin1) return null;
            const r = Math.min(pin1.size[0], pin1.size[1]) / 2 + 0.18;
            return (
              <circle
                cx={pin1.at[0]}
                cy={pin1.at[1]}
                r={r}
                fill="none"
                stroke={PCB_LAYER_PALETTE.pad}
                strokeWidth={0.08}
                strokeOpacity={focused ? 0.95 : 0.65}
                style={{ transition: theme.motion.base }}
              />
            );
          })()}
        </>
      ) : (
        <>
          {/* Stub mode — kind-keyed bounding rect for footprints
              ingested before we had pad/outline geometry. */}
          <rect
            x={-w / 2}
            y={-h / 2}
            width={w}
            height={h}
            rx={0.2}
            fill={layerStroke}
            fillOpacity={focused ? 0.32 : 0.16}
            stroke={layerStroke}
            strokeWidth={focused ? 0.18 : 0.1}
            style={{ transition: theme.motion.base }}
          />
          <circle
            cx={-w / 2 + pin1Inset}
            cy={-h / 2 + pin1Inset}
            r={Math.min(w, h) * 0.07}
            fill={PCB_LAYER_PALETTE.pad}
            opacity={layerVisible ? 1 : 0.4}
          />
        </>
      )}

      {/* Refdes label — counter-rotates to stay upright. The negative
          rotation cancels the parent rotation, so the text reads at 0°
          regardless of footprint orientation. Anchored at the body
          centroid (cx, cy) so the label tracks the actual pad
          envelope on off-center footprints. */}
      {showLabel && (
        <g transform={`rotate(${placement.rotation})`}>
          <text
            x={cx}
            y={cy + h / 2 + 1.2}
            textAnchor="middle"
            fontFamily={theme.fonts.mono}
            fontSize={Math.max(0.8, Math.min(w, h) * 0.35)}
            fill={layerStroke}
            fillOpacity={focused ? 1 : 0.75}
            style={{
              userSelect: "none",
              transition: theme.motion.base,
              letterSpacing: "-0.02em",
            }}
          >
            {refdes}
          </text>
        </g>
      )}
    </g>
  );
}

/** Memoised — there can be hundreds of footprints on a board, and most
 *  of them don't change between renders. The hover/select branches make
 *  this real: only the one or two focused footprints re-render on
 *  hover, the rest skip via prop equality. */
export const PCBFootprint = memo(PCBFootprintImpl);
